<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddLabel from './AddLabel.vue';
import EditLabel from './EditLabel.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedLabel = ref({});
const searchQuery = ref('');

const records = computed(() => getters['labels/getLabels'].value);
const uiFlags = computed(() => getters['labels/getUIFlags'].value);

// Filtered labels based on search
const filteredLabels = computed(() => {
  if (!searchQuery.value) return records.value;
  const query = searchQuery.value.toLowerCase();
  return records.value.filter(
    label =>
      label.title.toLowerCase().includes(query) ||
      (label.description && label.description.toLowerCase().includes(query))
  );
});

const deleteMessage = computed(() => ` ${selectedLabel.value.title}?`);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedLabel.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedLabel.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteLabel = async id => {
  try {
    await store.dispatch('labels/delete', id);
    useAlert(t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedLabel.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedLabel.value.id] = true;
  closeDeletePopup();
  deleteLabel(selectedLabel.value.id);
};

onBeforeMount(() => {
  store.dispatch('labels/get');
});
</script>

<template>
  <div class="settings-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 56px"
    >
      <div class="flex items-center gap-3">
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('LABEL_MGMT.HEADER') }}
        </h1>
        <span class="ws-count-badge">{{ records.length }}</span>
      </div>
      <button class="ws-btn" @click="openAddPopup">
        <span class="i-ph-plus-bold size-4" />
        {{ $t('LABEL_MGMT.HEADER_BTN_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div class="ws-desc px-6 py-3">
      <p class="text-sm" style="color: #4b5563; margin: 0">
        {{ $t('LABEL_MGMT.DESCRIPTION') }}
      </p>
    </div>

    <!-- Search -->
    <div class="ws-toolbar px-6 py-3">
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-sm">
          <span
            class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
            style="color: #9ca3af"
          />
          <input
            v-model="searchQuery"
            type="text"
            class="ws-search reset-base w-full pl-10 pr-4 py-2 text-sm rounded-lg"
            placeholder="Search labels..."
          />
        </div>
        <span class="text-sm" style="color: #6b7280">{{ filteredLabels.length }} labels</span>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6">
      <!-- Loading State -->
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('LABEL_MGMT.LOADING')" />
      </div>

      <!-- Empty State -->
      <div
        v-else-if="!filteredLabels.length"
        class="flex items-center justify-center py-20"
      >
        <p style="color: #6b7280">{{ $t('LABEL_MGMT.LIST.404') }}</p>
      </div>

      <!-- Labels Table -->
      <div v-else class="ws-table-wrap">
        <table class="ws-table">
          <thead class="ws-thead">
            <tr>
              <th class="ws-th">
                {{ $t('LABEL_MGMT.LIST.TABLE_HEADER.NAME') }}
              </th>
              <th class="ws-th">
                {{ $t('LABEL_MGMT.LIST.TABLE_HEADER.DESCRIPTION') }}
              </th>
              <th class="ws-th">
                {{ $t('LABEL_MGMT.LIST.TABLE_HEADER.COLOR') }}
              </th>
              <th class="ws-th ws-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="label in filteredLabels"
              :key="label.title"
              class="ws-tr"
            >
              <td class="ws-td font-medium" style="color: #111827">
                {{ label.title }}
              </td>
              <td class="ws-td" style="color: #4b5563; font-size: 12px">
                {{ label.description || '-' }}
              </td>
              <td class="ws-td">
                <div class="flex items-center gap-2">
                  <span
                    class="w-4 h-4 rounded border border-slate-200"
                    :style="{ backgroundColor: label.color }"
                  />
                  <span class="text-xs font-mono" style="color: #6b7280">{{
                    label.color
                  }}</span>
                </div>
              </td>
              <td class="ws-td ws-td--right">
                <div class="flex justify-end gap-1">
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                    icon="i-ph-pencil-simple-bold"
                    slate
                    xs
                    faded
                    :is-loading="loading[label.id]"
                    @click="openEditPopup(label)"
                  />
                  <Button
                    v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                    icon="i-ph-trash-bold"
                    xs
                    ruby
                    faded
                    :is-loading="loading[label.id]"
                    @click="openDeletePopup(label)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modals -->
    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddLabel @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditLabel :selected-response="selectedLabel" @close="hideEditPopup" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('LABEL_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('LABEL_MGMT.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<style scoped>
.settings-page,
.dark .settings-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-desc {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-count-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}
.ws-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  background: #ffffff;
}
.ws-table {
  border-collapse: collapse !important;
  width: 100%;
  background: #ffffff !important;
}
.ws-thead {
  background: #f9fafb !important;
  border-bottom: 1px solid #e5e7eb !important;
}
.ws-th {
  padding: 10px 24px !important;
  text-align: left !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
  color: #374151 !important;
  background: #f9fafb !important;
}
.ws-th--right {
  text-align: right !important;
}
.ws-tr {
  border-bottom: 1px solid #f3f4f6 !important;
  background: #ffffff !important;
  transition: background 0.12s ease;
}
.ws-tr:last-child {
  border-bottom: none !important;
}
.ws-tr:hover {
  background: #fafafa !important;
}
.ws-td {
  padding: 14px 24px !important;
  font-size: 13px !important;
  color: #111827 !important;
  vertical-align: middle !important;
  background: #ffffff;
}
.ws-td--right {
  text-align: right !important;
}
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
:deep(.modal-mask label) {
  color: #111827 !important;
}
:deep(.modal-mask input:not(.reset-base)),
:deep(.modal-mask select:not(.reset-base)),
:deep(.modal-mask textarea:not(.reset-base)) {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #111827 !important;
}
:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
